import React, { useState, useEffect } from 'react';
import { List, Copy, Trash2, Download } from 'lucide-react';

const LineCounterTool = () => {
  const [input, setInput] = useState('');
  const [stats, setStats] = useState(null);
  const [showNumbers, setShowNumbers] = useState(false);
  const [copied, setCopied] = useState(false);

  const analyzeLines = (text) => {
    if (!text) {
      setStats(null);
      return;
    }

    const allLines = text.split('\n');
    const nonEmptyLines = allLines.filter(line => line.trim().length > 0);
    const emptyLines = allLines.filter(line => line.trim().length === 0);
    const uniqueLines = [...new Set(nonEmptyLines)];
    const duplicateCount = nonEmptyLines.length - uniqueLines.length;

    // Line length analysis
    const lineLengths = nonEmptyLines.map(line => line.length);
    const avgLength = lineLengths.length > 0 
      ? Math.round(lineLengths.reduce((a, b) => a + b, 0) / lineLengths.length)
      : 0;
    const maxLength = lineLengths.length > 0 ? Math.max(...lineLengths) : 0;
    const minLength = lineLengths.length > 0 ? Math.min(...lineLengths) : 0;

    // Find longest line
    const longestLineIndex = lineLengths.indexOf(maxLength);
    const longestLine = nonEmptyLines[longestLineIndex] || '';

    setStats({
      totalLines: allLines.length,
      nonEmptyLines: nonEmptyLines.length,
      emptyLines: emptyLines.length,
      uniqueLines: uniqueLines.length,
      duplicateCount,
      avgLength,
      maxLength,
      minLength,
      longestLine: longestLine.substring(0, 50) + (longestLine.length > 50 ? '...' : ''),
      longestLineNum: longestLineIndex + 1
    });
  };

  useEffect(() => {
    analyzeLines(input);
  }, [input]);

  const getNumberedText = () => {
    return input.split('\n').map((line, i) => `${String(i + 1).padStart(4, ' ')} | ${line}`).join('\n');
  };

  const copyToClipboard = () => {
    const textToCopy = showNumbers ? getNumberedText() : input;
    navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadText = () => {
    const textToDownload = showNumbers ? getNumberedText() : input;
    const blob = new Blob([textToDownload], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'text-with-lines.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-gray-300">
            Enter Text
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showNumbers}
              onChange={(e) => setShowNumbers(e.target.checked)}
              className="w-4 h-4 rounded bg-gray-700 border-gray-600 text-blue-600"
            />
            <span className="text-sm text-gray-400">Show line numbers</span>
          </label>
        </div>
        
        <div className="relative">
          {showNumbers && input && (
            <div className="absolute left-0 top-0 bottom-0 w-12 bg-gray-900 border-r border-gray-700 rounded-l-lg pointer-events-none overflow-hidden">
              <div className="p-4 text-right text-gray-500 text-sm font-mono leading-6">
                {input.split('\n').map((_, i) => (
                  <div key={i}>{i + 1}</div>
                ))}
              </div>
            </div>
          )}
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Paste or type your text here to count lines..."
            className={`w-full h-64 bg-gray-800 border border-gray-700 rounded-lg p-4 text-white placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 font-mono text-sm leading-6 ${
              showNumbers && input ? 'pl-16' : ''
            }`}
          />
        </div>
      </div>

      {stats && (
        <div className="space-y-4">
          {/* Main Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-gray-800 rounded-lg text-center">
              <div className="text-3xl font-bold text-blue-400">{stats.totalLines}</div>
              <div className="text-xs text-gray-500">Total Lines</div>
            </div>
            <div className="p-4 bg-gray-800 rounded-lg text-center">
              <div className="text-3xl font-bold text-green-400">{stats.nonEmptyLines}</div>
              <div className="text-xs text-gray-500">Non-Empty</div>
            </div>
            <div className="p-4 bg-gray-800 rounded-lg text-center">
              <div className="text-3xl font-bold text-yellow-400">{stats.emptyLines}</div>
              <div className="text-xs text-gray-500">Empty Lines</div>
            </div>
            <div className="p-4 bg-gray-800 rounded-lg text-center">
              <div className="text-3xl font-bold text-purple-400">{stats.uniqueLines}</div>
              <div className="text-xs text-gray-500">Unique Lines</div>
            </div>
          </div>

          {/* Additional Stats */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 bg-gray-800 rounded-lg">
              <h3 className="text-sm font-medium text-gray-300 mb-3">Line Length Statistics</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Average Length:</span>
                  <span className="text-white">{stats.avgLength} chars</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Longest Line:</span>
                  <span className="text-white">{stats.maxLength} chars (line {stats.longestLineNum})</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Shortest Line:</span>
                  <span className="text-white">{stats.minLength} chars</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Duplicate Lines:</span>
                  <span className="text-white">{stats.duplicateCount}</span>
                </div>
              </div>
            </div>

            {stats.longestLine && (
              <div className="p-4 bg-gray-800 rounded-lg">
                <h3 className="text-sm font-medium text-gray-300 mb-3">Longest Line Preview</h3>
                <div className="text-sm text-gray-400 font-mono break-all">
                  {stats.longestLine}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex gap-3">
        <button
          onClick={copyToClipboard}
          disabled={!input}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
        >
          <Copy size={16} />
          {copied ? 'Copied!' : 'Copy'}
        </button>
        <button
          onClick={downloadText}
          disabled={!input}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
        >
          <Download size={16} />
          Download
        </button>
        <button
          onClick={() => setInput('')}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
        >
          <Trash2 size={16} />
          Clear
        </button>
      </div>
    </div>
  );
};

export default LineCounterTool;
